<template>
  <div class="tags-input">
    <div v-if="value.length" class="tags-input__chips" data-test="tag-chips">
      <span v-for="tag in value" :key="tag" class="tags-input__chip">
        {{ tag }}
        <button
          type="button"
          class="tags-input__remove"
          :aria-label="`Remove tag ${tag}`"
          data-test="remove-tag"
          @click="remove(tag)"
        >
          ×
        </button>
      </span>
    </div>

    <b-form-input
      v-model="draft"
      :maxlength="maxLength"
      :disabled="atLimit"
      :placeholder="
        atLimit ? `Up to ${maxTags} tags` : 'Type a tag and press Enter'
      "
      autocomplete="off"
      data-test="tag-input"
      @input="onInput"
      @keydown.enter.native.prevent="commit"
      @keydown.native="onKeydown"
      @blur="commit"
    />

    <div v-if="unusedSuggestions.length" class="tags-input__suggestions">
      <span class="tags-input__suggestions-label">Suggestions</span>
      <button
        v-for="tag in unusedSuggestions"
        :key="tag"
        type="button"
        class="tags-input__suggestion"
        data-test="suggestion"
        @click="add(tag)"
      >
        {{ tag }}
      </button>
    </div>
    <div v-else-if="showExample && !value.length" class="tags-input__hint">
      For example: swms, jha, insurance
    </div>
  </div>
</template>

<script>
import {
  PROJECT_FILE_MAX_TAGS,
  PROJECT_FILE_MAX_TAG_LENGTH,
  normalizeTags,
} from '@/utils/projectFiles'

/**
 * Free-text tags: chips plus an input that commits on Enter, comma or blur.
 * v-model is a normalised string[] (lowercase, trimmed, unique, capped) — the
 * same rules the API applies, so what the user sees is what gets stored.
 */
export default {
  name: 'ProjectFileTagsInput',

  props: {
    value: { type: Array, default: () => [] },
    // Tags already in use on the project, offered as one-click additions.
    suggestions: { type: Array, default: () => [] },
    // The "For example" line; a form group that already has its own help text turns it off.
    showExample: { type: Boolean, default: true },
  },

  data() {
    return {
      draft: '',
      maxTags: PROJECT_FILE_MAX_TAGS,
      maxLength: PROJECT_FILE_MAX_TAG_LENGTH,
    }
  },

  computed: {
    atLimit() {
      return this.value.length >= this.maxTags
    },

    unusedSuggestions() {
      if (this.atLimit) return []
      return normalizeTags(this.suggestions)
        .filter((tag) => this.value.indexOf(tag) === -1)
        .slice(0, 8)
    },
  },

  methods: {
    emit(tags) {
      this.$emit('input', normalizeTags(tags))
    },

    add(tag) {
      this.emit(this.value.concat([tag]))
    },

    remove(tag) {
      this.emit(this.value.filter((t) => t !== tag))
    },

    // Commit whatever is typed. A blank draft does nothing.
    commit() {
      const text = this.draft
      this.draft = ''
      if (!text || !text.trim() || this.atLimit) return
      this.add(text)
    },

    // Typing or pasting a comma commits everything before it, so
    // "swms, jha" pasted in becomes two tags.
    onInput(text) {
      if (typeof text !== 'string' || text.indexOf(',') === -1) return
      const parts = text.split(',')
      const rest = parts.pop()
      this.draft = rest
      this.emit(this.value.concat(parts))
    },

    // Backspace in an empty input removes the last tag.
    onKeydown(event) {
      if (event.key === 'Backspace' && !this.draft && this.value.length) {
        this.remove(this.value[this.value.length - 1])
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tags-input__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.tags-input__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.75;
}

.tags-input__remove {
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
}

.tags-input__suggestions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.tags-input__suggestions-label,
.tags-input__hint {
  color: #6b7280;
  font-size: 0.75rem;
}

.tags-input__hint {
  margin-top: 6px;
}

.tags-input__suggestion {
  padding: 0 10px;
  border: 1px dashed #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.75;
  cursor: pointer;

  &:hover {
    border-color: #2c66d5;
    color: #2c66d5;
  }
}
</style>
